// src/contexts/SettingsContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../apiService';

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
    const [practiceSize, setPracticeSizeState] = useState(null);
    const [appSettings, setAppSettingsState] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Function to get organizationId from JWT
    const getOrganizationId = () => {
        const token = localStorage.getItem('lipay_afya_token');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return payload.organization_id ? parseInt(payload.organization_id) : null;
            } catch (e) {
                console.error('Failed to parse JWT:', e);
                return null;
            }
        }
        return null;
    };

    // Effect to fetch initial settings on mount
    useEffect(() => {
        const fetchInitialSettings = async () => {
            setIsLoading(true);
            setError(null);
            const organizationId = getOrganizationId();

            if (!organizationId) {
                console.warn('No organizationId found in JWT. Using default settings.');
                setPracticeSizeState('clinic');
                setAppSettingsState({
                    id: null,
                    practiceSize: 'clinic',
                    enableStaffManagement: true,
                    enableInventoryManagement: true,
                    enableAdvancedReports: true,
                    enableTelemedicine: true,
                    enableDetailedMedicalHistory: true,
                    organizationName: 'Default Hospital Name',
                    organizationLogoUrl: 'https://placehold.co/40x40/007bff/ffffff?text=HL',
                    address: null,
                    phone: null,
                    email: null,
                });
                setIsLoading(false);
                return;
            }

            try {
                const fetchedSettings = await apiService.getSettings(organizationId);
                setPracticeSizeState(fetchedSettings.practiceSize || 'clinic');
                setAppSettingsState(fetchedSettings);
            } catch (error) {
                console.error('Failed to fetch initial settings:', error);
                setError('Failed to load settings. Using defaults.');
                setPracticeSizeState('clinic');
                setAppSettingsState({
                    id: organizationId,
                    practiceSize: 'clinic',
                    enableStaffManagement: true,
                    enableInventoryManagement: true,
                    enableAdvancedReports: true,
                    enableTelemedicine: true,
                    enableDetailedMedicalHistory: true,
                    organizationName: 'Default Hospital Name',
                    organizationLogoUrl: 'https://placehold.co/40x40/007bff/ffffff?text=HL',
                    address: null,
                    phone: null,
                    email: null,
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchInitialSettings();
    }, []);

    // Effect to adjust appSettings based on practiceSize changes
    useEffect(() => {
        if (!isLoading && practiceSize !== null && appSettings !== null) {
            let newSettings = { ...appSettings, practiceSize };
            switch (practiceSize) {
                case 'solo':
                    newSettings = {
                        ...newSettings,
                        enableStaffManagement: false,
                        enableInventoryManagement: false,
                        enableAdvancedReports: false,
                        enableTelemedicine: true,
                        enableDetailedMedicalHistory: true,
                    };
                    break;
                case 'clinic':
                    newSettings = {
                        ...newSettings,
                        enableStaffManagement: true,
                        enableInventoryManagement: true,
                        enableAdvancedReports: false,
                        enableTelemedicine: true,
                        enableDetailedMedicalHistory: true,
                    };
                    break;
                case 'hospital':
                    newSettings = {
                        ...newSettings,
                        enableStaffManagement: true,
                        enableInventoryManagement: true,
                        enableAdvancedReports: true,
                        enableTelemedicine: true,
                        enableDetailedMedicalHistory: true,
                    };
                    break;
                default:
                    break;
            }
            if (JSON.stringify(newSettings) !== JSON.stringify(appSettings)) {
                setAppSettingsState(newSettings);
                const organizationId = getOrganizationId();
                if (organizationId) {
                    apiService.updateSettings(organizationId, newSettings).catch((e) => {
                        console.error('Failed to persist settings:', e);
                        setError('Failed to save settings. Please try again.');
                    });
                } else {
                    setError('No organization ID available. Settings not saved.');
                }
            }
        }
    }, [practiceSize, isLoading, appSettings]);

    // Function to update practice size
    const setPracticeSize = async (size) => {
        setPracticeSizeState(size);
    };

    // Function to update individual setting
    const updateSetting = async (key, value) => {
        const updatedSettings = { ...appSettings, [key]: value };
        setAppSettingsState(updatedSettings);
        const organizationId = getOrganizationId();
        if (!organizationId) {
            setError('No organization ID available. Settings not saved.');
            return;
        }
        try {
            await apiService.updateSettings(organizationId, updatedSettings);
        } catch (error) {
            console.error('Failed to update setting:', error);
            setError('Failed to save settings. Please try again.');
            setAppSettingsState(appSettings); // Revert state on failure
        }
    };

    return (
        <SettingsContext.Provider
            value={{
                practiceSize,
                setPracticeSize,
                appSettings,
                updateSetting,
                isLoading,
                error,
            }}
        >
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => useContext(SettingsContext);